import { QueryClient, DefaultOptions } from '@tanstack/react-query';
import { showToast } from '@/utils/toast';

// React Query 기본 옵션
const queryConfig: DefaultOptions = {
  queries: {
    retry: (failureCount, error) => {
      // 401, 403 에러는 재시도하지 않음
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status === 401 || status === 403) return false;
      }
      
      // 최대 2번까지 재시도
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 30 * 60 * 1000, // 30분 (가비지 컬렉션 시간)
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  },
  mutations: {
    retry: 1,
    retryDelay: 1000,
  },
};

// React Query 클라이언트 설정
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

// 자주 사용하는 쿼리 키 상수
export const QUERY_KEYS = {
  dashboard: (period: string, region?: string) => ['dashboard', period, region] as const,
  orderStatusCounts: (period: string, region?: string) => ['order-status-counts', period, region] as const,
  recentOrders: (period: string, region?: string) => ['recent-orders', period, region] as const,
  globalItems: (page: number, perPage: number) => ['global-items', page, perPage] as const,
  boostHistory: (hours: number, region?: string) => ['boost-history', hours, region] as const,
} as const;

// 캐시 무효화 헬퍼 함수들
export const invalidateQueries = {
  dashboard: () => queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  orderStatusCounts: () => queryClient.invalidateQueries({ queryKey: ['order-status-counts'] }),
  recentOrders: () => queryClient.invalidateQueries({ queryKey: ['recent-orders'] }),
  globalItems: () => queryClient.invalidateQueries({ queryKey: ['global-items'] }),
  boostHistory: () => queryClient.invalidateQueries({ queryKey: ['boost-history'] }),
  
  // 전체 무효화
  all: () => queryClient.invalidateQueries(),
};

// 미리 fetch 헬퍼 함수들
export const prefetchQueries = {
  dashboard: (period: string = 'month', region?: string) => {
    return queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.dashboard(period, region),
      queryFn: () => import('@/lib/api').then(({ getOrderDashboard }) => getOrderDashboard({ period: period as any, region: region as any })),
      staleTime: 5 * 60 * 1000,
    });
  },
  
  orderStatusCounts: (period: string = 'week', region?: string) => {
    return queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.orderStatusCounts(period, region),
      queryFn: () => import('@/lib/api').then(({ getOrderStatusCounts }) => getOrderStatusCounts({ period: period as any, region: region as any })),
      staleTime: 2 * 60 * 1000,
    });
  },
};